import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { buildSearchParams } from '../_helpers/utils';
import { APIResponse } from '../_types/api-responses.types';
import {
  DocumentModel,
  DocumentSummary,
  SearchDocsRequest,
  SearchSignedContractDocumentsParams,
  SignedContractDocument,
} from '../_types/document.types';
import { UserAuthService } from './user-auth.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private readonly baseUrl: string;

  private readonly baseHeaders = new HttpHeaders().set(
    'content-type',
    'application/json'
  );

  constructor(
    private readonly httpClient: HttpClient,
    private readonly userAuthService: UserAuthService
  ) {
    this.baseUrl = environment.DOCUMENTAPIURL;
  }

  private get headers(): HttpHeaders {
    return this.baseHeaders.set(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
  }

  getDocument(id: number): Observable<APIResponse<DocumentModel>> {
    return this.httpClient.get<APIResponse<DocumentModel>>(
      `${this.baseUrl}get/doc/id/${id}`,
      { headers: this.headers }
    );
  }

  /**
   * One customer's stored documents, most recent first, for a picker.
   *
   * Staff only, and the unpublished ones answer unless the caller opts out:
   * invoices, reminders, signed offers and inbound attachments are stored
   * that way, and a back-office picker wants exactly those. The backend
   * defaults the other way round, for the customer-facing listings.
   */
  searchDocuments(
    body: SearchDocsRequest
  ): Observable<APIResponse<{ documents: DocumentSummary[] }>> {
    return this.httpClient.post<APIResponse<{ documents: DocumentSummary[] }>>(
      `${this.baseUrl}get/docs/search`,
      { includeUnpublished: true, ...body },
      { headers: this.headers }
    );
  }

  searchSignedContractDocuments(
    familyMemberId: string,
    params: SearchSignedContractDocumentsParams
  ): Observable<APIResponse<{ documents: SignedContractDocument[] }>> {
    const searchParams = buildSearchParams({
      insuranceType: params.insuranceType,
      contractType: params.contractType,
      searchText: params.searchText,
      limit: params.limit ?? 20,
    });
    return this.httpClient.get<
      APIResponse<{ documents: SignedContractDocument[] }>
    >(
      `${
        this.baseUrl
      }contract/family-member/${familyMemberId}/signed?${searchParams.toString()}`,
      { headers: this.headers }
    );
  }
}
